// Maps a user record (as returned by getUserById) back into the grouped
// checkbox arrays the Create/Update forms bind to. The permission model is a
// flat set of boolean columns on the user row, but the forms present them as
// grouped checklists — these functions are the boolean-columns -> form-arrays
// direction, and the `value.xPermission?.includes('y')` expressions in the
// submit handlers are the inverse.
//
// Extracted here so the Create form's "copy permissions from an existing
// user" flow reuses exactly the same mapping the Update form already relies
// on, rather than a second, drifting copy of it.

function stringToArray(value) {
	return value ? value.split(',') : [];
}

function getCompanySubmissionInitValue(user) {
	const res = [];
	if (user?.canAssignUserToCompanyProposal === true) res.push('canAssignUser');
	if (user?.canUploadDoneFileToCompanyProposal === true) res.push('canUploadDoneFile');
	if (user?.canViewListReportToCompanyProposal === true) res.push('canViewListReport');
	if (user?.canAutoreassignProposal === true) res.push('canAutoreassignProposal');
	return res;
}

function getComplaintInitValue(user) {
	const res = [];
	if (user?.canUploadDoneFileToComplaint === true) res.push('canUploadDoneFile');
	if (user?.canAssignUserToComplaint === true) res.push('canAssignComplaint');
	if (user?.canViewComplaintReport === true) res.push('canViewReportForComplaint');
	return res;
}

function getIssuedLetterInitValue(user) {
	const res = [];
	if (user?.canCreateIssuedLetter === true) res.push('canCreateIssuedLetter');
	if (user?.canUpdateIssuedLetter === true) res.push('canUpdateIssuedLetter');
	if (user?.canDeleteIssuedLetter === true) res.push('canDeleteIssuedLetter');
	return res;
}

function getLeaveRequestPermissionInitValue(user) {
	const res = [];
	if (user?.canApproveRejectLeaveRequest === true) res.push('canApproveRejectLeaveRequest');
	if (user?.canViewLeaveRequestReport === true) res.push('canViewLeaveRequestReport');
	if (user?.canNotApplyForLeave === true) res.push('canNotApplyForLeave');
	return res;
}

function getPaymentPermissionInitValue(user) {
	const res = [];
	if (user?.paymentAgentRegistration === true) res.push('paymentAgentRegistration');
	if (user?.paymentPublicService === true) res.push('paymentPublicService');
	if (user?.paymentLicense === true) res.push('paymentLicense');
	if (user?.canUpdateLicense === true) res.push('canUpdateLicense');
	if (user?.paymentFine === true) res.push('paymentFine');
	if (user?.canUpdateFine === true) res.push('canUpdateFine');
	return res;
}

function getAgentPermissionInitValue(user) {
	const res = [];
	if (user?.canReviewAgentRequest === true) res.push('canReviewAgentRequest');
	if (user?.canApproveRejectDeactivateAgent === true) res.push('canApproveRejectDeactivateAgent');
	if (user?.canFinalApproveRejectAgent === true) res.push('canFinalApproveRejectAgent');
	return res;
}

function getCompanySubmissionReportInitValue(user) {
	const res = [];
	if (user?.canAssignUserToCompanyReport === true) res.push('canAssignUser');
	return res;
}

function getInitCompanyPermission(user) {
	const res = [];
	if (user?.canUpdateCompanyLicensing) res.push('canUpdateLicensing');
	if (user?.canUpdateCompanyGovernance) res.push('canUpdateGovernance');
	if (user?.canUpdateCompanyContact) res.push('canUpdateContact');
	return res;
}

// Every permission-bearing form field, derived from one source user. Identity
// fields (username/password/fullName) are deliberately excluded — copying a
// permission set must never carry over who the person is.
//
// `includePlacement` controls whether department/office/position come along:
// they drive which nested permission checklists are even visible, but copying
// them from a source in a different office would misfile the new user, so the
// caller decides.
//
// `positionOptions` is needed only alongside `includePlacement`, and only for
// forms whose Position field is a react-select (Create) rather than a plain
// select (Update): the former binds to an { value, label } option object and
// reads back `position.value` on submit, so handing it the bare DB string
// would silently submit an empty position. Pass the same option list the field
// renders and the matching option is selected instead.
function getPermissionValuesFromUser(user, { includePlacement = false, positionOptions = null } = {}) {
	const values = {
		role: stringToArray(user?.role),
		companyReportType: stringToArray(user?.companyReportType),
		financialReportTypes: stringToArray(user?.financialReportTypes),
		companyProposalPermission: getCompanySubmissionInitValue(user),
		companyReportPermission: getCompanySubmissionReportInitValue(user),
		complaintPermission: getComplaintInitValue(user),
		companyPermission: getInitCompanyPermission(user),
		requestLeavePermission: getLeaveRequestPermissionInitValue(user),
		agentPermission: getAgentPermissionInitValue(user),
		paymentPermission: getPaymentPermissionInitValue(user),
		issuedLetter: getIssuedLetterInitValue(user),
		// mdd/ews/nprp are JSON array columns, not comma-joined strings.
		mdd: user?.mdd || [],
		ewsPermission: user?.ews || [],
		nprpPermission: user?.nprp || [],
	};

	if (includePlacement) {
		values.department = stringToArray(user?.department);
		values.office = user?.office || '';
		if (positionOptions) {
			values.position = positionOptions.find((option) => option?.value?.toString() === user?.position?.toString()) || '';
		} else {
			values.position = user?.position || '';
		}
	}

	return values;
}

export {
	stringToArray,
	getCompanySubmissionInitValue,
	getComplaintInitValue,
	getIssuedLetterInitValue,
	getLeaveRequestPermissionInitValue,
	getPaymentPermissionInitValue,
	getAgentPermissionInitValue,
	getCompanySubmissionReportInitValue,
	getInitCompanyPermission,
	getPermissionValuesFromUser,
};
